/**
 * Reply video modal controller for YouTube triggers.
 *
 * Usage:
 * - Any element with `data-video-trigger` plus one of:
 *   - `data-video-url="https://www.youtube.com/watch?v=ID"` (or embed URL)
 *   - `data-video-id="ID"` and `data-video-type="youtube"`
 *   - `data-video-playlist="PLAYLIST_ID"` (optional, YouTube only)
 *
 * Deep links (any page, e.g. from email):
 * - `?video=ID` or `#video=ID` opens that YouTube video in the modal on load.
 * - `video` may also be a full YouTube URL (watch, youtu.be, shorts, embed).
 * - Optional: `t=90` / `t=90s` start offset.
 * - The video must already be on the page (a trigger for it exists); otherwise nothing opens.
 * - Deep-link opens are muted (browsers block unmuted autoplay without a click).
 *
 * Behavior:
 * - Click opens modal and injects iframe.
 * - Click flow requests unmuted playback via YouTube Player API.
 * - Close button, backdrop click, and `dialog:close` unload iframe.
 */
(function () {
  const SELECTORS = {
    modal: '#video-modal',
    frame: '#ni-video-modal-iframe',
    close: '[data-video-modal-close]',
    trigger: '[data-video-trigger], a[href*="youtube.com/embed/"]',
    awpPlayingContainer: '.awp1v-player-embed.awp1v-player--playing',
    awpPauseButton: 'button.awp1v-player--pause'
  };

  const YOUTUBE_HOST_REGEX = /youtube\.com|youtu\.be/;
  const YOUTUBE_ID_REGEX = /^[A-Za-z0-9_-]{6,}$/;

  class ReplyVideoModal {
    /**
     * Stop active AWPlayer v1 audio before video playback starts.
     */
    #pauseActiveAudio() {
      const awpV1PlayingContainer = document.querySelector(SELECTORS.awpPlayingContainer);
      if (!(awpV1PlayingContainer instanceof HTMLElement)) return;

      const pauseButton = awpV1PlayingContainer.querySelector(SELECTORS.awpPauseButton);
      if (pauseButton instanceof HTMLButtonElement) {
        pauseButton.click();
      }
    }

    /**
     * Bind all listeners for modal open/close lifecycle.
     */
    init() {
      this.#attachDelegatedClicks();
      this.#attachCloseHandlers();
      if (document.readyState === 'loading') {
        window.addEventListener('DOMContentLoaded', () => this.#initFromUrl());
      } else {
        this.#initFromUrl();
      }
    }

    /**
     * Build a YouTube embed URL for modal playback.
     * @param {{ id: string, playlist?: string, start?: number, autoplay?: number, mute?: number }} param0
     * @returns {string}
     */
    #ytEmbedUrl({ id, playlist, start, autoplay = 1, mute = 1 }) {
      const params = new URLSearchParams({
        autoplay: String(autoplay),
        mute: String(mute),
        rel: '0',
        enablejsapi: '1'
      });

      if (start) params.set('start', String(start));

      if (playlist) {
        params.set('playlist', playlist || id);
        params.set('loop', '1');
      }

      return `https://www.youtube.com/embed/${id}?${params.toString()}`;
    }

    /**
     * Extract a YouTube video/playlist id from a URL.
     * @param {string} url
     * @returns {{ id: string, playlist?: string } | null}
     */
    #parseYoutubeUrl(url) {
      try {
        const parsedUrl = new URL(url, window.location.origin);
        if (!YOUTUBE_HOST_REGEX.test(parsedUrl.hostname)) return null;

        const parts = parsedUrl.pathname.split('/').filter(Boolean);
        let id = parsedUrl.searchParams.get('v') || '';
        if (!id && ['embed', 'shorts', 'live'].includes(parts[0])) id = parts[1] || '';
        if (!id && parsedUrl.hostname === 'youtu.be') id = parts[0] || '';

        const playlist = parsedUrl.searchParams.get('list') || undefined;
        return id ? { id, playlist } : null;
      } catch (_error) {
        // Ignore invalid trigger URLs.
        return null;
      }
    }

    /**
     * Parse video payload from trigger dataset/href.
     * @param {Element} target
     * @returns {{ type: string, id: string, playlist?: string } | null}
     */
    #parseVideoFromDataset(target) {
      const element = /** @type {HTMLElement} */ (target);
      let url = element.dataset.videoUrl;
      let type = element.dataset.videoType;
      let id = element.dataset.videoId;
      let playlist = element.dataset.videoPlaylist;

      // RTE links may not provide data attributes; use href as source URL.
      if (!url && target.tagName === 'A' && /** @type {HTMLAnchorElement} */ (target).href) {
        url = /** @type {HTMLAnchorElement} */ (target).href;
      }

      if (!id && url) {
        const parsed = this.#parseYoutubeUrl(url);
        if (parsed) {
          type = 'youtube';
          id = parsed.id;
          playlist ||= parsed.playlist;
        }
      }

      if (!type || !id) return null;
      return { type, id, playlist };
    }

    /**
     * Create iframe and attach unmute command for click flow.
     * @param {{ type: string, id: string, playlist?: string, start?: number, mute?: number }} video
     * @returns {HTMLIFrameElement}
     */
    #buildIframe({ type, id, playlist, start, mute }) {
      let src = '';
      if (type === 'youtube') src = this.#ytEmbedUrl({ id, playlist, start, mute });

      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.title = 'Video player';

      if (type === 'youtube' && mute === 0) {
        const sendUnmute = () => {
          iframe.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'unMute', args: [] }),
            '*'
          );
          iframe.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
            '*'
          );
        };

        iframe.addEventListener('load', () => {
          sendUnmute();
        });
      }

      return iframe;
    }

    /**
     * Open modal and inject iframe.
     * @param {{ type: string, id: string, playlist?: string, start?: number, mute?: number }} video
     */
    #openVideo({ type, id, playlist, start, mute = 1 }) {
      // Non-gesture opens (e.g. URL params) should stay muted by default.
      // Modal markup is rendered globally via `reply-video-modal` in `layout/theme.liquid`.
      const modal = document.querySelector(SELECTORS.modal);
      const frame = document.querySelector(SELECTORS.frame);
      if (!modal || !frame) {
        // eslint-disable-next-line no-console
        console.warn('ReplyVideoModal: Modal markup missing. Check `reply-video-modal` render in layout/theme.liquid.');
        return;
      }

      this.#pauseActiveAudio();

      frame.innerHTML = '';
      frame.appendChild(this.#buildIframe({ type, id, playlist, start, mute }));
      if ('showDialog' in modal && typeof modal.showDialog === 'function') {
        modal.showDialog();
      }
    }

    /**
     * Close modal and unload iframe.
     */
    #closeVideo() {
      const modal = document.querySelector(SELECTORS.modal);
      const frame = document.querySelector(SELECTORS.frame);
      if (!modal || !frame) return;

      frame.innerHTML = '';
      if ('closeDialog' in modal && typeof modal.closeDialog === 'function') {
        modal.closeDialog();
      }
    }

    /**
     * Listen for clicks on all video triggers.
     */
    #attachDelegatedClicks() {
      document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;

        const trigger = event.target.closest(SELECTORS.trigger);
        if (!trigger) return;

        event.preventDefault();
        const info = this.#parseVideoFromDataset(trigger);
        // Click is an explicit user gesture, so request unmuted playback.
        if (info) this.#openVideo({ ...info, mute: 0 });
      });
    }

    /**
     * Bind close interactions (button, backdrop, custom close event).
     */
    #attachCloseHandlers() {
      document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;

        if (event.target.closest(SELECTORS.close)) {
          event.preventDefault();
          this.#closeVideo();
        }
      });

      document.addEventListener('click', (event) => {
        const modal = document.querySelector(SELECTORS.modal);
        if (!modal) return;
        const dialog = modal.querySelector('dialog');
        // Clicking dialog backdrop closes and unloads the player.
        if (event.target === dialog) this.#closeVideo();
      });

      document.addEventListener('dialog:close', (event) => {
        const modal = document.querySelector(SELECTORS.modal);
        if (event.target !== modal) return;

        // Ensure iframe is cleared for any external dialog close trigger.
        const frame = document.querySelector(SELECTORS.frame);
        if (frame) frame.innerHTML = '';
      });
    }

    /**
     * Optionally open a video from query/hash parameters.
     */
    async #initFromUrl() {
      const url = new URL(window.location.href);
      const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
      const search = url.searchParams;
      /**
       * @param {string} key
       * @returns {string | null}
       */
      const get = (key) => search.get(key) || hash.get(key);

      const rawVideo = (get('video') || '').trim();
      if (!rawVideo) return;

      // Only YouTube is supported; ignore any other `type`.
      if ((get('type') || 'youtube').toLowerCase() !== 'youtube') return;

      let id = '';
      if (YOUTUBE_ID_REGEX.test(rawVideo)) {
        id = rawVideo;
      } else {
        const parsed = this.#parseYoutubeUrl(rawVideo);
        if (parsed) {
          id = parsed.id;
        }
      }
      if (!YOUTUBE_ID_REGEX.test(id)) return;

      // Only open videos that are actually embedded on this page. The page's own trigger
      // supplies the playlist, so a link cannot attach one.
      const onPage = [...document.querySelectorAll(SELECTORS.trigger)]
        .map((trigger) => this.#parseVideoFromDataset(trigger))
        .find((video) => video && video.type === 'youtube' && video.id === id);
      if (!onPage) return;

      // `t=90` or `t=90s`; ignore anything else (e.g. `1m30s`).
      const startMatch = /^(\d+)s?$/.exec(get('t') || '');
      const start = startMatch ? Number(startMatch[1]) : undefined;

      // The modal is a custom element; make sure it is upgraded before calling showDialog().
      await customElements.whenDefined('dialog-component');
      this.#openVideo({ type: 'youtube', id, playlist: onPage.playlist, start });
    }
  }

  new ReplyVideoModal().init();
})();
